import { ReactNode, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMobileFilter } from '../contexts/MobileFilterContext';
import { useLayoutTier } from '../hooks/useLayoutTier';
import { cn } from '../utils/className';
import {
  FilterPanelContext,
  FilterPanelFooter,
  FilterPanelHeader,
} from './filters/FilterPanelContext';

interface FiltersMobileProps {
  children: ReactNode;
  onClear?: () => void;
  /** Shown on "See results". The filters apply as they change. */
  resultCount?: number;
}

/**
 * The filter panel for the phone and the tablet. It slides in from the right
 * and covers a phone. A field with options opens a second level that slides in
 * over the first.
 */
export function FiltersMobile({
  children,
  onClear,
  resultCount,
}: FiltersMobileProps) {
  const { isOpen, close } = useMobileFilter();
  const { isTabletDown } = useLayoutTier();
  const [shouldRender, setShouldRender] = useState(false);
  const [detailHost, setDetailHost] = useState<HTMLDivElement | null>(null);
  const [detailActive, setDetailActive] = useState(false);

  const panel = useMemo(
    () => ({ detailHost, setDetailActive, close, resultCount }),
    [detailHost, close, resultCount]
  );

  if (!isTabletDown) return null;

  // Keep component mounted if opening or if we need to animate out
  const show = isOpen || shouldRender;

  const handleAnimationEnd = () => {
    if (!isOpen) {
      setShouldRender(false);
      setDetailActive(false);
    }
  };

  // Show immediately when opening
  if (isOpen && !shouldRender) {
    setShouldRender(true);
  }

  if (!show) return null;

  const state = isOpen ? 'open' : 'closed';

  return createPortal(
    <FilterPanelContext.Provider value={panel}>
      {/* Overlay */}
      <div
        data-mobile-filter-overlay
        data-state={state}
        className='fixed inset-0 z-40 bg-neutral-900/50'
        onClick={close}
        onAnimationEnd={handleAnimationEnd}
      />

      <aside
        data-mobile-filter
        data-state={state}
        aria-label='Filters'
        className='bg-surface-primary fixed top-0 right-0 z-50 flex h-full
          w-full flex-col overflow-hidden md:w-[420px]'
      >
        <FilterPanelHeader
          title='Filters'
          onBack={close}
          backLabel='Close filters'
        />

        <div className='flex flex-1 flex-col gap-6 overflow-y-auto p-4'>
          {children}
        </div>

        <FilterPanelFooter resetLabel='Reset filters' onReset={onClear} />

        {/* Second level. A field portals its options in here. */}
        <div
          ref={setDetailHost}
          inert={!detailActive}
          className={cn(
            'bg-surface-primary absolute inset-0 flex flex-col',
            'transition-transform duration-200 ease-out',
            detailActive ? 'translate-x-0' : 'translate-x-full'
          )}
        />
      </aside>
    </FilterPanelContext.Provider>,
    document.body
  );
}
